/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, memo, useMemo, useRef, useState } from 'react'
import debounce from 'lodash/debounce'
import History from '@mui/icons-material/History'
import Keyboard from '@mui/icons-material/Keyboard'
import {
  Stack,
  Alert,
  Tooltip,
  IconButton,
  TextFieldProps,
  Box,
  InputAdornment,
  Theme,
} from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import { pickerNoneOption } from '@/hooks/usePickerFieldData'
import useTableHotKeys from '@/hooks/useTableHotKeys'
import { DocumentRow } from '@/types/documents'
import { EntrataVendor } from '@/types/entrata-vendor'
import { ProjectGridField as TSField } from '@/types/fields'
import { LiveListEntity } from '@/types/live-lists'
import { getFormattedValue } from '@/utils/data-type'
import { APPROX_CHAR_WIDTH } from '@/utils/field'
import { INVOICE_VENDOR_INFO_TYPE_ID } from '@/utils/info-types'
import { useCurrentAccountContext } from '@/components/accounts/CurrentAccountProvider'
import FlagsBulletList from '@/components/flags/FlagsBulletList'
import { useDocumentComparisonDataContext } from '@/components/image-zoom-pan/providers/DocumentComparisonDataProvider'
import { useDocumentImagePageContext } from '@/components/image-zoom-pan/providers/DocumentImagePageProvider'
import { useDocumentContext } from '@/components/image-zoom-pan/providers/DocumentProvider'
import { useValidationOptionsContext } from '@/components/image-zoom-pan/providers/ValidationOptionsProvider'
import TextInputField from '@/components/inputs/TextInputField'
import { Dialog, DialogContent } from '../dialog'
import { useDocumentCurrentStateViewContext } from '../image-zoom-pan/providers/DocumentCurrentStateViewProvider'
import FieldPortalProvider from './FieldPortalProvider'
import CategoryField from './fields/CategoryField'
import CheckboxField from './fields/CheckboxField'
import CurrencyField from './fields/CurrencyField'
import DateField from './fields/DateField'
import DateTimeField from './fields/DateTimeField'
import DecimalField from './fields/DecimalField'
import EntrataLiveVendorList from './fields/EntrataLiveVendorList'
import IntegerField from './fields/IntegerField'
import ListLookupField from './fields/ListLookupField'
import LiveListPickerField, {
  liveListNoneOption,
} from './fields/LiveListPickerField'
import PhoneNumberTextField from './fields/PhoneNumberTextField'
import PickerField from './fields/PickerField'
import TimeField from './fields/TimeField'
import FieldUpdatingIndicator from './FieldUpdatingIndicator'
import FieldWithChipsAndSuggestions from './FieldWithChipsAndSuggestions'
import FocusContainer from './FocusContainer'
import { useDocumentChipsContext } from './providers/DocumentChipsProvider'
import { useDocumentRowsContext } from './providers/DocumentRowsProvider'
import { useDocumentsChangeSetsContext } from './providers/DocumentsChangeSetsProvider'
import { useFieldErrorContext } from './providers/FieldErrorProvider'
import { useSelectedFieldContext } from './providers/SelectedFieldProvider'
import useFieldValue from './useFieldValue'
import { ENTRATA_ORG_ID } from './VisibleCardsControl'

const ENTRATA_VENDOR_INFO_TYPE_ID = '0195cf0b-50b8-3005-53e8-d40e3b123597'

type FieldProps = {
  field: TSField
  documentRow?: DocumentRow
  isInTable?: boolean
  fieldPosition?: {
    column: number
    row: number
  }
  tableGrid?: {
    columns: number
    rows: number
  }
  tableBodyEl?: HTMLTableSectionElement | null
  deleteDocumentRows?: (rowIds: string[]) => void
  insertDocumentRow?: (position: 'before' | 'after') => void
}

const fieldTypesThatAllowChipSuggestions = [
  'cc_num',
  'text',
  'phone',
  'int',
  'decimal',
  'currency',
]

function getFieldHeaderColor(
  fieldId: string,
  palette: Theme['palette'],
  differentFields?: Set<string>,
  isInTable?: boolean,
) {
  if (!differentFields || isInTable) {
    return undefined
  }

  const isDifferentField =
    differentFields.has('all') || differentFields.has(fieldId)

  const color = isDifferentField ? palette.error.main : palette.success.main
  return `${color} !important`
}

function shouldBeMultiline(containerWidth: number, valueLength: number) {
  const charsThatFit = Math.floor(containerWidth / APPROX_CHAR_WIDTH)
  return valueLength >= charsThatFit
}

function Field({
  field,
  documentRow,
  isInTable,
  fieldPosition,
  tableGrid,
  tableBodyEl,
  deleteDocumentRows,
  insertDocumentRow,
}: FieldProps) {
  const { project_grid_field_type } = field
  const fieldCode = project_grid_field_type.code
  const focusContainerRef = useRef<HTMLDivElement>(null)
  const { setSelectedField, isSelectedField } = useSelectedFieldContext() || {}
  const { isDisabled, isLite } = useDocumentContext()
  const { baseRow } = useDocumentRowsContext()
  const { getFieldError } = useFieldErrorContext()
  const { differentFieldsAnsRowsComparingRowValues, setBaseFieldHeight } =
    useDocumentComparisonDataContext()
  const { rootOrganization } = useCurrentAccountContext()
  const { documentCurrentStateView } = useDocumentCurrentStateViewContext()
  const { setImagePage } = useDocumentImagePageContext()
  const { autoNavigateToPageOnFieldFocus } = useValidationOptionsContext()
  const valueOverlay = useOverlay()

  // documentRowFromContext is second because there is always one for the base grid,
  // so we don't want to override the value passed for fields in table
  const currentDocumentRow = documentRow || baseRow

  const inputRef = useRef<HTMLInputElement>(null)
  const hadManualValueRef = useRef<boolean | null>(null)
  const [searchValue, setSearchValue] = useState('')

  const debounceSetValue = useMemo(() => debounce(setSearchValue, 200), [])

  const { getFieldChips, setHoveredChipId } = useDocumentChipsContext()
  const { isFieldUpdatingForIndicator } = useDocumentsChangeSetsContext()

  const [hasSolidBackground, setHasSolidBackground] = useState(false)
  const [disableArrowKeys, setDisableArrowKeys] = useState(false)

  const isSelected =
    !!currentDocumentRow &&
    !!isSelectedField?.(field.id, currentDocumentRow?.id)

  const fieldIsBeingUpdated = isFieldUpdatingForIndicator(
    field.id,
    currentDocumentRow?.id,
  )

  const fieldError = currentDocumentRow
    ? getFieldError(field.id, currentDocumentRow?.id)
    : ''

  const {
    value,
    refId,
    dataLineageValues,
    documentRowValue,
    hasManualValue,
    jsonValue,
    onManualChange,
    onCategoryChange,
    clearValue,
    wasAutoFilled,
  } = useFieldValue({
    field,
    documentRowId: currentDocumentRow?.id,
  })

  const chips = useMemo(
    () =>
      field && currentDocumentRow?.id
        ? getFieldChips(field.id, currentDocumentRow?.id)
        : [],
    [field, getFieldChips, currentDocumentRow?.id],
  )
  const row_value_flags = documentRowValue?.row_value_flags || []

  // This is memoized because this render logic could be potentially expensive when rendering lots of fields
  const renderedField = useMemo(() => {
    const listLookupFieldRule = field.project_grid_field_rules?.find(
      ({ rule }) => rule.code === 'list_lookup',
    )

    const commonProps = {
      value,
      color:
        fieldCode !== 'live-list-picker' &&
        field.input_behavior === 'manual_only'
          ? ('indigo' as const)
          : undefined,
      'data-focusid': documentRowValue?.id,
      disabled:
        isDisabled ||
        (fieldCode === 'picker'
          ? field.input_behavior === 'manual_not_allowed' ||
            (!field.metadata.data_list_column_id &&
              !field.params?.data_list_column_names)
          : field.input_behavior === 'manual_not_allowed'),
      variant: (isInTable ? 'standard' : 'filled') as TextFieldProps['variant'],
      size: 'small' as TextFieldProps['size'],
      sx: {
        width: '100%',
        '.Mui-disabled': {
          color: (theme: Theme) =>
            getFieldHeaderColor(
              field.id,
              theme.palette,
              differentFieldsAnsRowsComparingRowValues?.fieldIds,
              isInTable,
            ),
        },
      },
      onChange: (
        inputField: string | null,
        refId?: string | null,
        jsonObject?: Record<string, any> | null,
      ) => {
        if (field.input_behavior !== 'manual_only') {
          if (hadManualValueRef.current === null) {
            hadManualValueRef.current = hasManualValue
          }
          if (
            fieldTypesThatAllowChipSuggestions.includes(fieldCode) &&
            !listLookupFieldRule
          ) {
            const lastWord = inputField?.toString().split(' ').slice(-1)[0]
            debounceSetValue(lastWord || '')
          }
        }
        onManualChange({ manualValue: inputField, refId, jsonObject })
      },
      onClick: (e: React.MouseEvent) => {
        if (field.input_behavior === 'manual_not_allowed') {
          e.stopPropagation()
          focusContainerRef.current?.focus()
        }
      },
      inputRef,
      label: isInTable ? undefined : field.name,
      error: !!fieldError,
      helperText: fieldError,
      InputLabelProps: {
        shrink: true,
      },
      inputProps: isInTable
        ? {
            style: {
              padding: 0,
              opacity: wasAutoFilled ? 0.5 : 1,
            },
          }
        : {},
      InputProps: {
        disableUnderline: isInTable,
        startAdornment:
          field.input_behavior === 'manual_only' &&
          fieldCode !== 'category' &&
          fieldCode !== 'live-list-picker' ? (
            <Tooltip title="Manual Entry Only" placement="top" enterDelay={500}>
              <InputAdornment
                position="start"
                sx={{
                  mt:
                    listLookupFieldRule || fieldCode === 'picker'
                      ? '-2px !important'
                      : undefined,
                  ml:
                    listLookupFieldRule || fieldCode === 'picker'
                      ? '4px !important'
                      : undefined,
                }}
              >
                <Keyboard />
              </InputAdornment>
            </Tooltip>
          ) : undefined,
        sx: {
          fontSize: isInTable ? 12 : undefined,
          borderBottom: (theme: Theme) =>
            `1px solid ${getFieldHeaderColor(
              field.id,
              theme.palette,
              differentFieldsAnsRowsComparingRowValues?.fieldIds,
              isInTable,
            )}`,
        },
      },
    }

    if (listLookupFieldRule) {
      return (
        <ListLookupField
          {...commonProps}
          value={
            value === null
              ? null
              : {
                  label: value,
                  id: refId || 'add-new-list-item',
                }
          }
          isShowingChips={isSelected}
          fieldRule={listLookupFieldRule}
          documentRowValue={documentRowValue}
          setHasSolidBackground={setHasSolidBackground}
          setDisableArrowKeys={setDisableArrowKeys}
          isInTable={isInTable}
        />
      )
    }

    switch (fieldCode) {
      case 'address':
      case 'cc_num':
      case 'text': {
        const containerWidth = focusContainerRef.current?.clientWidth || 0
        const isMultiline =
          documentCurrentStateView !== 'validation' &&
          !isInTable &&
          shouldBeMultiline(containerWidth, value.length)
        return (
          <TextInputField
            {...commonProps}
            multiline={isMultiline}
            maxRows={isMultiline ? 4 : undefined}
          />
        )
      }
      case 'phone':
        return <PhoneNumberTextField {...commonProps} />
      case 'int':
        return <IntegerField {...commonProps} />
      case 'decimal':
        return <DecimalField {...commonProps} />
      case 'currency':
        return <CurrencyField {...commonProps} />
      case 'date':
        return <DateField isInTable={isInTable} {...commonProps} />
      case 'time':
        return <TimeField isInTable={isInTable} {...commonProps} />
      case 'datetime':
        return <DateTimeField isInTable={isInTable} {...commonProps} />
      case 'category':
        return (
          <CategoryField
            {...commonProps}
            field={field}
            isInTable={isInTable}
            onChange={onCategoryChange}
            setDisableArrowKeys={setDisableArrowKeys}
          />
        )
      case 'checkbox':
        return (
          <CheckboxField
            {...commonProps}
            ref={inputRef}
            isSelected={isSelected}
            wasAutoFilled={wasAutoFilled}
            isInTable={isInTable}
          />
        )
      case 'picker':
        return (
          <PickerField
            {...commonProps}
            value={
              refId
                ? {
                    label: value,
                    id: refId,
                  }
                : value === '' && dataLineageValues.final_value === ''
                ? pickerNoneOption
                : null
            }
            field={field}
            setDisableArrowKeys={setDisableArrowKeys}
            isInTable={isInTable}
          />
        )
      case 'live-list-picker': {
        const jsonData =
          value === '' && dataLineageValues.final_value === ''
            ? liveListNoneOption
            : jsonValue

        if (
          rootOrganization?.id === ENTRATA_ORG_ID &&
          [ENTRATA_VENDOR_INFO_TYPE_ID, INVOICE_VENDOR_INFO_TYPE_ID].includes(
            field.info_type_id || '',
          )
        ) {
          return (
            <EntrataLiveVendorList
              disabled={commonProps.disabled}
              jsonValue={jsonData as LiveListEntity<EntrataVendor>}
              onManualChange={onManualChange}
            />
          )
        }
        return currentDocumentRow ? (
          <LiveListPickerField
            {...commonProps}
            value={jsonData as LiveListEntity}
            field={field}
            setDisableArrowKeys={setDisableArrowKeys}
            isInTable={isInTable}
            documentRow={currentDocumentRow}
          />
        ) : null
      }
      default: {
        return (
          <Alert severity="error" variant="outlined" sx={{ width: '100%' }}>
            Missing field type: {fieldCode}
          </Alert>
        )
      }
    }
  }, [
    field,
    value,
    fieldCode,
    documentRowValue,
    isDisabled,
    isInTable,
    fieldError,
    wasAutoFilled,
    differentFieldsAnsRowsComparingRowValues?.fieldIds,
    onManualChange,
    hasManualValue,
    debounceSetValue,
    refId,
    isSelected,
    onCategoryChange,
    dataLineageValues.final_value,
    documentCurrentStateView,
    jsonValue,
    currentDocumentRow,
    rootOrganization?.id,
  ])

  useTableHotKeys({
    cellPosition: fieldPosition,
    documentRow: currentDocumentRow,
    tableGrid,
    tableBodyEl,
    deleteDocumentRows,
    insertDocumentRow,
    enabled: !!isInTable && isSelected && !disableArrowKeys,
  })

  const canRevert = hasManualValue && !isDisabled
  const cantHaveChips =
    // checking in case someone used postman to update the input_behavior of a live list picker field
    fieldCode === 'live-list-picker' ||
    field.input_behavior === 'manual_only' ||
    isLite

  const formattedValue = useMemo(() => {
    return getFormattedValue(value, fieldCode)
  }, [value, fieldCode])

  useEffect(() => {
    setHoveredChipId(null)
    setSearchValue('')
    hadManualValueRef.current = null
  }, [isSelected, setHoveredChipId])

  // Track field height for comparison purposes (only for non-table fields)
  useEffect(() => {
    if (
      isInTable ||
      !focusContainerRef.current ||
      !currentDocumentRow?.document_id ||
      !setBaseFieldHeight
    )
      return

    const element = focusContainerRef.current
    const resizeObserver = new ResizeObserver(() => {
      const height = element.getBoundingClientRect().height
      setBaseFieldHeight(field.id, currentDocumentRow.document_id, height)
    })

    resizeObserver.observe(element)

    // Initial measurement
    const height = element.getBoundingClientRect().height
    setBaseFieldHeight(field.id, currentDocumentRow.document_id, height)

    return () => {
      resizeObserver.disconnect()
    }
  }, [field.id, currentDocumentRow?.document_id, isInTable, setBaseFieldHeight])

  return (
    <>
      <FieldPortalProvider hidePortal={cantHaveChips}>
        <FocusContainer
          focusContainerRef={focusContainerRef}
          isSelectedField={isDisabled ? false : isSelected}
          isUserEditable={field.input_behavior !== 'manual_not_allowed'}
          onFocus={() => {
            if (isDisabled || !currentDocumentRow) return

            setSelectedField?.({
              document_row_id: currentDocumentRow?.id,
              field,
            })

            if (
              documentCurrentStateView !== 'validation' ||
              !autoNavigateToPageOnFieldFocus
            )
              return

            const firstAiPage = [
              ...new Set([
                ...(documentRowValue?.ai_page_numbers || []),
                ...chips.map((chip) => chip.document_page_number),
              ]),
            ].sort((a, b) => a - b)[0]

            if (firstAiPage) {
              setImagePage(() => firstAiPage)
            }
          }}
          onBlur={() => {
            setSelectedField?.(null)
          }}
        >
          <Tooltip
            placement="top"
            slotProps={{
              popper: {
                onClick: () => {
                  valueOverlay.open()
                },
              },
              tooltip: {
                sx: {
                  cursor: 'pointer',
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  overflowWrap: 'anywhere',
                },
              },
            }}
            title={formattedValue ? formattedValue : ''}
            enterDelay={1000}
            enterNextDelay={1000}
          >
            <Stack
              direction="row"
              spacing={1}
              flexGrow={1}
              alignItems={isInTable ? 'flex-start' : 'center'}
            >
              {cantHaveChips || isDisabled ? (
                <Stack flexGrow={1}>
                  {renderedField}
                  {fieldIsBeingUpdated && <FieldUpdatingIndicator />}
                  {row_value_flags.length > 0 && !isDisabled && !isLite && (
                    <FlagsBulletList rowValueFlags={row_value_flags} />
                  )}
                </Stack>
              ) : (
                <>
                  <FieldWithChipsAndSuggestions
                    chips={chips}
                    hasSolidBackground={hasSolidBackground}
                    isSelected={isSelected}
                    documentRowId={currentDocumentRow?.id}
                    fieldId={field.id}
                    searchValue={searchValue}
                    onAssign={() => {
                      if (hadManualValueRef.current === false) {
                        clearValue()
                        setSearchValue('')
                      }
                    }}
                    onDelete={() => {
                      inputRef.current?.focus()
                    }}
                    isInTable={isInTable}
                  >
                    {renderedField}
                    {fieldIsBeingUpdated && <FieldUpdatingIndicator />}
                    {row_value_flags.length > 0 && (
                      <FlagsBulletList rowValueFlags={row_value_flags} />
                    )}
                  </FieldWithChipsAndSuggestions>
                </>
              )}

              {canRevert && (
                <Box sx={{ flexShrink: 0, py: isInTable ? 0 : 0.5 }}>
                  <Tooltip
                    title={
                      field.input_behavior === 'manual_only'
                        ? 'Clear'
                        : 'Revert to System Results'
                    }
                  >
                    <IconButton
                      size="small"
                      tabIndex={-1}
                      onClick={() => {
                        inputRef.current?.focus()
                        clearValue()
                        setSearchValue('')
                        hadManualValueRef.current = null
                      }}
                      sx={{
                        mt: isInTable ? -0.5 : undefined,
                      }}
                    >
                      <History fontSize={isInTable ? 'inherit' : 'medium'} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Stack>
          </Tooltip>
        </FocusContainer>
      </FieldPortalProvider>
      <Dialog
        {...valueOverlay}
        maxWidth="xs"
        title={`${isInTable ? `Row ${fieldPosition?.row} - ` : ''}${
          field.name
        }`}
      >
        <DialogContent>{formattedValue}</DialogContent>
      </Dialog>
    </>
  )
}

export default memo(Field)
